import client from './graphQlClient';
import {
    ORDER_RECENT_CHANGES_FOR_DATASET_QUERY,
    MAKE_ORDER_UNRUMOURED_MUTATION,
    MAKE_ORDER_RUMOURED_MUTATION,
    UPDATE_ORDER_RUMOURED_FIELDS_MUTATION,
    CLONE_ORDER_MUTATION,
    MAKE_WITHDRAWN_MUTATION,
    MAKE_NEW_MUTATION,
    MAKE_ACTIVE_MUTATION,
} from './queries/OrderQueries';
import moment from 'moment';
import logger from '_legacy/diagnostics/logging/logger';
import { displayOnlyRumouredHandler } from './apiHelpers';
import { snapshotApi } from './snapshotApi';

const ConvertedOrdersToBeHideInHours = 24;

export class OrderApi {
    static CONTEXT = {
        errorHandling: {
            unknownResult: {
                resolution: {
                    isSafeToRetry: true,
                },
            },
        },
    };
    static FETCH_POLICY = 'no-cache';

    static fetchOrders = async (
        groupId,
        datasetId,
        numberOfYears,
        cancelToken,
        includeDeleted,
        includeConverted,
        displayOnlyRumoured
    ) => {
        const changesSinceSnapshotPromise = (async () => {
            try {
                const resultPromise = client().query({
                    query: ORDER_RECENT_CHANGES_FOR_DATASET_QUERY,
                    variables: { groupId, numberOfYears },
                    fetchPolicy: this.FETCH_POLICY,
                });

                const result = await Promise.race([
                    resultPromise,
                    cancelToken.promise,
                ]);

                if (result.reason) {
                    logger.error(
                        'Result  changesSinceSnapshotPromise: ',
                        result
                    );
                    throw result;
                }

                return result.data;
            } catch (error) {
                logger.error('Error changesSinceSnapshotPromise: ', error);
                throw error;
            }
        })();

        const snapshotPromise = (async () => {
            return await snapshotApi.fetchOrdersSnapshot({
                datasetId,
                numberOfYears,
            });
        })();

        const {
            publicOrdersRecentlyChanged = [],
            groupOnlyOrders = [],
            ordersWithPrivateComments = [],
            individualOrders = [],
            unavailableOrderIdsFromLastSnapshot = [],
        } = (await changesSinceSnapshotPromise) || {};

        let orders = await snapshotPromise;

        const m = new Map();

        orders.forEach((i) => m.set(i.id, i));
        unavailableOrderIdsFromLastSnapshot.forEach((o) => m.delete(o));
        publicOrdersRecentlyChanged.forEach((o) => m.set(o.id, o));
        groupOnlyOrders.forEach((o) => m.set(o.id, o));
        ordersWithPrivateComments.forEach((o) => m.set(o.id, o));
        individualOrders.forEach((o) => m.set(o.id, o));

        displayOnlyRumouredHandler(m, displayOnlyRumoured);

        if (!includeDeleted) {
            m.forEach((o) => {
                if (o.state === 'Deleted' || o.state === 'Withdrawn') {
                    m.delete(o.id);
                }
            });
        }
        if (!includeConverted) {
            m.forEach((o) => {
                if (o.state === 'Converted') {
                    m.delete(o.id);
                }
            });
        } else {
            const earliestDateToShowConverted = moment
                .utc()
                .add(-ConvertedOrdersToBeHideInHours, 'hours')
                .format('YYYY-MM-DD');

            m.forEach((o) => {
                if (
                    o.state === 'Converted' &&
                    o.updatedDateTime < earliestDateToShowConverted
                ) {
                    m.delete(o.id);
                }
            });
        }

        orders = Array.from(m.values());

        return orders;
    };

    static makeUnRumoured = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_ORDER_UNRUMOURED_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeOrderUnRumoured);

    static makeRumoured = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_ORDER_RUMOURED_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeOrderRumoured);

    static updateRumouredFields = (client, variables) =>
        client
            .mutate({
                mutation: UPDATE_ORDER_RUMOURED_FIELDS_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.updateOrderRumouredFields);

    static cloneOrder = (client, variables) =>
        client
            .mutate({
                mutation: CLONE_ORDER_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.cloneOrder);

    static makeWithdrawn = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_WITHDRAWN_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeOrderWithdrawn);

    static makeNew = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_NEW_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeOrderNew);

    static makeActive = (client, variables) =>
        client
            .mutate({
                mutation: MAKE_ACTIVE_MUTATION,
                variables,
                context: this.CONTEXT,
                fetchPolicy: this.FETCH_POLICY,
            })
            .then((result) => result.data.makeOrderActive);
}
